import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmDialog = ({
  title = "Confirm Action",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "danger", // danger, warning, primary
}) => {
  const variants = {
    danger: {
      icon: 'var(--danger-600)',
      iconBg: 'var(--danger-100)',
      confirmBtn: 'btn-danger',
    },
    warning: {
      icon: 'var(--warning-600)',
      iconBg: 'var(--warning-100)',
      confirmBtn: 'btn-warning',
    },
    primary: {
      icon: 'var(--primary-600)',
      iconBg: 'var(--primary-100)',
      confirmBtn: 'btn-primary',
    },
  };

  const style = variants[variant] || variants.danger;

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <button className="confirm-dialog-close" onClick={onCancel}>
          <X size={20} />
        </button>

        <div className="confirm-dialog-icon" style={{ backgroundColor: style.iconBg, color: style.icon }}>
          <AlertTriangle size={32} />
        </div>

        <div className="confirm-dialog-content">
          <h3 className="confirm-dialog-title">{title}</h3>
          <p className="confirm-dialog-message">{message}</p>
        </div>

        <div className="confirm-dialog-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            {cancelText}
          </button>
          <button className={`btn ${style.confirmBtn}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>

      <style jsx>{`
        .confirm-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1rem;
          animation: fadeIn 0.2s ease-out;
        }

        .confirm-dialog {
          background: white;
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-2xl);
          max-width: 400px;
          width: 100%;
          padding: 2rem;
          position: relative;
          animation: slideUp 0.3s ease-out;
        }

        .confirm-dialog-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: var(--gray-400);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-fast);
        }

        .confirm-dialog-close:hover {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }

        .confirm-dialog-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .confirm-dialog-content {
          text-align: center;
          margin-bottom: 2rem;
        }

        .confirm-dialog-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--gray-900);
          margin: 0 0 0.5rem 0;
        }

        .confirm-dialog-message {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin: 0;
          line-height: 1.5;
        }

        .confirm-dialog-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .confirm-dialog-actions .btn {
          flex: 1;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 480px) {
          .confirm-dialog {
            padding: 1.5rem;
          }

          .confirm-dialog-actions {
            flex-direction: column-reverse;
          }

          .confirm-dialog-actions .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ConfirmDialog;

